// src/pages/finance-portal/chart-of-accounts/ChartOfAccounts.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Chip, IconButton, Alert, CircularProgress, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, FormControlLabel, Switch,
    Collapse, Tooltip, InputAdornment,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const TYPE_COLORS = {
    ASSET: '#22c55e',
    LIABILITY: '#ef4444',
    EQUITY: '#8b5cf6',
    REVENUE: '#3b82f6',
    EXPENSE: '#f59e0b',
};

const TYPE_LABELS = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

function AccountRow({ account, depth = 0, onEdit, onArchive, allAccounts }) {
    const [open, setOpen] = useState(true);
    const hasChildren = account.children && account.children.length > 0;
    const typeColor = TYPE_COLORS[account.account_type] || '#64748b';

    return (
        <>
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2,
                    pl: 2 + depth * 3,
                    borderBottom: '1px solid #f1f5f9',
                    '&:hover': { bgcolor: '#f8fafc' },
                    transition: 'background 0.15s',
                }}
            >
                {hasChildren ? (
                    <IconButton size="small" onClick={() => setOpen(!open)} sx={{ p: 0.25 }}>
                        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                ) : (
                    <Box sx={{ width: 28 }} />
                )}

                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#64748b', minWidth: 60 }}>
                    {account.account_number}
                </Typography>
                <Typography sx={{
                    fontFamily, fontSize: '0.85rem', flex: 1,
                    fontWeight: account.is_header ? 700 : 500,
                    color: account.is_header ? '#1e293b' : '#475569',
                }}>
                    {account.name}
                </Typography>
                <Chip
                    label={account.account_type}
                    size="small"
                    sx={{
                        fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20,
                        bgcolor: `${typeColor}15`, color: typeColor,
                    }}
                />
                {account.system_account && (
                    <Chip label="System" size="small" sx={{ fontFamily, fontSize: '0.6rem', height: 18, bgcolor: '#e2e8f0', color: '#64748b' }} />
                )}
                <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(account)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {!account.system_account && (
                    <Tooltip title="Archive">
                        <IconButton size="small" onClick={() => onArchive(account)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                            <ArchiveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {hasChildren && (
                <Collapse in={open} timeout={200}>
                    {account.children.map(child => (
                        <AccountRow key={child.id} account={child} depth={depth + 1} onEdit={onEdit} onArchive={onArchive} allAccounts={allAccounts} />
                    ))}
                </Collapse>
            )}
        </>
    );
}

export default function ChartOfAccounts() {
    const [accounts, setAccounts] = useState([]);
    const [flatAccounts, setFlatAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSetup, setIsSetup] = useState(null);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [settingUp, setSettingUp] = useState(false);

    // Dialog state
    const [dialog, setDialog] = useState({ open: false, mode: 'add', account: null });
    const [formData, setFormData] = useState({ account_number: '', name: '', description: '', account_type: 'ASSET', parent_id: '', is_header: false });
    const [saving, setSaving] = useState(false);

    // Archive dialog
    const [archiveDialog, setArchiveDialog] = useState({ open: false, account: null });
    const [archiving, setArchiving] = useState(false);

    const fetchSetupStatus = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa/setup-status');
            if (res.data.success) setIsSetup(res.data.is_setup);
        } catch (err) { console.error(err); }
    }, []);

    const fetchAccounts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = {};
            if (search) params.search = search;
            if (typeFilter) params.type = typeFilter;

            const [treeRes, flatRes] = await Promise.all([
                axiosInstance.get('/api/finance/coa', { params }),
                axiosInstance.get('/api/finance/coa', { params: { ...params, flat: true } }),
            ]);

            if (treeRes.data.success) setAccounts(treeRes.data.data);
            if (flatRes.data.success) setFlatAccounts(flatRes.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load accounts');
        } finally {
            setIsLoading(false);
        }
    }, [search, typeFilter]);

    useEffect(() => { fetchSetupStatus(); }, [fetchSetupStatus]);
    useEffect(() => { if (isSetup) fetchAccounts(); else setIsLoading(false); }, [isSetup, fetchAccounts]);

    const handleSetupTemplate = async () => {
        setSettingUp(true);
        setError(null);
        try {
            const res = await axiosInstance.post('/api/finance/coa/setup-template', {});
            if (res.data.success) {
                setIsSetup(true);
                await fetchAccounts();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to initialize');
        } finally {
            setSettingUp(false);
        }
    };

    const openAddDialog = () => {
        setFormData({ account_number: '', name: '', description: '', account_type: 'ASSET', parent_id: '', is_header: false });
        setDialog({ open: true, mode: 'add', account: null });
    };

    const openEditDialog = (account) => {
        setFormData({
            account_number: account.account_number,
            name: account.name,
            description: account.description || '',
            account_type: account.account_type,
            parent_id: account.parent_id || '',
            is_header: account.is_header,
        });
        setDialog({ open: true, mode: 'edit', account });
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const payload = { ...formData, parent_id: formData.parent_id || null };
            if (dialog.mode === 'add') {
                await axiosInstance.post('/api/finance/coa', payload);
            } else {
                await axiosInstance.put(`/api/finance/coa/${dialog.account.id}`, payload);
            }
            setDialog({ open: false, mode: 'add', account: null });
            await fetchAccounts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save account');
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async () => {
        setArchiving(true);
        try {
            await axiosInstance.delete(`/api/finance/coa/${archiveDialog.account.id}`);
            setArchiveDialog({ open: false, account: null });
            await fetchAccounts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to archive account');
        } finally {
            setArchiving(false);
        }
    };

    // Header accounts for parent dropdown (filtered by selected type)
    const parentOptions = flatAccounts.filter(a => a.is_header && a.account_type === formData.account_type);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AccountTreeIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Chart of Accounts
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            Manage your accounting structure
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Setup Wizard */}
                {isSetup === false && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', textAlign: 'center', py: 6 }}>
                        <CardContent>
                            <PlaylistAddCheckIcon sx={{ fontSize: 56, color: ACCENT, mb: 2 }} />
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.2rem', color: '#1e293b', mb: 1 }}>
                                Initialize Your Chart of Accounts
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b', mb: 3, maxWidth: 500, mx: 'auto' }}>
                                Start with a standard business template including Assets, Liabilities, Equity, Revenue, and Expense accounts. You can customize it after setup.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleSetupTemplate}
                                disabled={settingUp}
                                startIcon={settingUp ? <CircularProgress size={18} color="inherit" /> : <AccountTreeIcon />}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px', px: 4, py: 1.5, bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                {settingUp ? 'Setting up...' : 'Initialize Default Template'}
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Accounts List */}
                {isSetup && (
                    <>
                        {/* Toolbar */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="Search accounts..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                                    sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' },
                                }}
                                sx={{ minWidth: { xs: '100%', sm: 220 } }}
                            />
                            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
                                <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Type</InputLabel>
                                <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} label="Type" sx={{ fontFamily, borderRadius: '10px' }}>
                                    <MenuItem value="">All Types</MenuItem>
                                    {TYPE_LABELS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <Box sx={{ flex: 1 }} />
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={openAddDialog}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                Add Account
                            </Button>
                        </Box>

                        {/* Tree */}
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden', overflowX: 'auto' }}>
                            {/* Header Row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, px: 2, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <Box sx={{ width: 28 }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', minWidth: 60, textTransform: 'uppercase', letterSpacing: '0.05em' }}>No.</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', flex: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Account Name</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 80 }}>Type</Typography>
                                <Box sx={{ width: 80 }} />
                            </Box>

                            {isLoading ? (
                                <Box sx={{ p: 3 }}>{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} height={40} sx={{ mb: 0.5 }} />)}</Box>
                            ) : accounts.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8' }}>No accounts found</Typography>
                                </Box>
                            ) : (
                                accounts.map(account => (
                                    <AccountRow key={account.id} account={account} onEdit={openEditDialog} onArchive={(a) => setArchiveDialog({ open: true, account: a })} allAccounts={flatAccounts} />
                                ))
                            )}
                        </Card>
                    </>
                )}
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog open={dialog.open} onClose={() => setDialog({ open: false, mode: 'add', account: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    {dialog.mode === 'add' ? 'Add Account' : 'Edit Account'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Account Number" size="small" fullWidth
                            value={formData.account_number} onChange={e => setFormData({ ...formData, account_number: e.target.value })}
                            InputProps={{ sx: { fontFamily } }} InputLabelProps={{ sx: { fontFamily } }}
                            placeholder="Auto-generated if empty"
                        />
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontFamily }}>Type</InputLabel>
                            <Select value={formData.account_type} onChange={e => setFormData({ ...formData, account_type: e.target.value, parent_id: '' })} label="Type" sx={{ fontFamily }}>
                                {TYPE_LABELS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                    <TextField label="Account Name" size="small" fullWidth required
                        value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                        InputProps={{ sx: { fontFamily } }} InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Description" size="small" fullWidth multiline rows={2}
                        value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                        InputProps={{ sx: { fontFamily } }} InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontFamily }}>Parent Account</InputLabel>
                        <Select value={formData.parent_id} onChange={e => setFormData({ ...formData, parent_id: e.target.value })} label="Parent Account" sx={{ fontFamily }}>
                            <MenuItem value="">None (Root Account)</MenuItem>
                            {parentOptions.map(a => <MenuItem key={a.id} value={a.id}>{a.account_number} - {a.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={<Switch checked={formData.is_header} onChange={e => setFormData({ ...formData, is_header: e.target.checked })} sx={{ '& .Mui-checked': { color: ACCENT }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Header/Group Account (cannot post transactions)</Typography>}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialog({ open: false, mode: 'add', account: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !formData.name || !formData.account_type}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {saving ? 'Saving...' : dialog.mode === 'add' ? 'Add Account' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Archive Confirmation */}
            <Dialog open={archiveDialog.open} onClose={() => setArchiveDialog({ open: false, account: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Archive {archiveDialog.account?.name}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                        This account will be hidden from forms and reports. Accounts with existing transactions cannot be archived.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setArchiveDialog({ open: false, account: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleArchive} disabled={archiving}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {archiving ? 'Archiving...' : 'Archive'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
